import React, { useState, useEffect, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Checkbox,
  Chip,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useQuery } from '@tanstack/react-query';
import { categoriesApi, CategoryApiResponse } from '../../services/categoriesApi';
import {
  getTextFieldStyles,
  getAutocompleteStyles,
  getAutocompleteListboxStyles,
} from '../../styles/formStyles';

const uncheckedIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface CategorySelectorProps {
  value: string[];
  onChange: (categories: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
}

const CategorySelector: React.FC<CategorySelectorProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder = '',
  label,
  error = false,
  helperText,
  required = false,
}) => {
  const [selectedNames, setSelectedNames] = useState<string[]>(value || []);

  const { data: categories, isLoading } = useQuery({
    queryKey: ['categories-all'],
    queryFn: () => categoriesApi.getAllCategories(),
    staleTime: 60_000,
  });

  const categoryList = useMemo(() => categories || [], [categories]);

  useEffect(() => {
    setSelectedNames(value || []);
  }, [value]);

  const selectedOptions = useMemo(
    () => categoryList.filter((c) => selectedNames.includes(c.name)),
    [categoryList, selectedNames]
  );

  const handleChange = (_event: any, newValue: CategoryApiResponse[]) => {
    const names = newValue.map((c) => c.name);
    setSelectedNames(names);
    onChange(names);
  };

  return (
    <Autocomplete<CategoryApiResponse, true, false, false>
      multiple
      disableCloseOnSelect
      options={categoryList}
      value={selectedOptions}
      onChange={handleChange}
      disabled={disabled}
      loading={isLoading}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option.id}>
          <Checkbox
            icon={uncheckedIcon}
            checkedIcon={checkedIcon}
            checked={selected}
            sx={{ mr: 1, color: 'text.secondary', '&.Mui-checked': { color: 'primary.main' } }}
          />
          {option.name}
        </li>
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, onDelete } = getTagProps({ index });
          return (
            <Chip
              key={key}
              label={option.name}
              size="small"
              onDelete={onDelete}
              sx={{
                backgroundColor: 'rgba(0, 169, 140, 0.15)',
                color: 'primary.main',
                borderColor: 'primary.main',
                border: '1px solid',
                '& .MuiChip-deleteIcon': { color: 'primary.main' },
              }}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={selectedOptions.length === 0 ? placeholder : ''}
          error={error}
          helperText={helperText}
          required={required}
          fullWidth
          margin="normal"
          sx={{
            ...getTextFieldStyles({ error }),
            '& .MuiAutocomplete-tag': { color: 'text.primary' },
          }}
        />
      )}
      fullWidth
      sx={getAutocompleteStyles()}
      ListboxProps={{ sx: getAutocompleteListboxStyles() }}
    />
  );
};

export default CategorySelector;
